/**
 * ERP Contacts Tab Component
 * Internal and external emergency contacts
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Phone, Mail, Users, Plus, Search, Building } from 'lucide-react';

export default function ERPContactsTab({ 
  contacts,
  searchQuery,
  onSearchChange,
  onShowContactDialog
}) {
  const filteredContacts = contacts.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.organization?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const internalContacts = filteredContacts.filter(c => c.contact_type === 'internal');
  const externalContacts = filteredContacts.filter(c => c.contact_type === 'external');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search contacts..."
            className="pl-10 bg-slate-800/50 border-slate-700"
          />
        </div>
        <Button onClick={onShowContactDialog}>
          <Plus className="w-4 h-4 mr-2" /> Add Contact
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Internal Contacts */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building className="w-5 h-5" /> Internal Contacts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              {internalContacts.map(contact => (
                <div key={contact.id} className="p-3 rounded-lg bg-slate-900/50 mb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-medium">{contact.name}</p>
                      <p className="text-sm text-slate-400">{contact.title}</p>
                      <p className="text-xs text-blue-400 capitalize">{contact.category?.replace('_', ' ')}</p>
                    </div>
                    {contact.is_primary && <Badge className="bg-blue-500/20 text-blue-300">Primary</Badge>}
                  </div>
                  <div className="mt-2 flex gap-4 text-sm">
                    <span className="flex items-center gap-1 text-slate-400">
                      <Phone className="w-3 h-3" /> {contact.primary_phone}
                    </span>
                    <span className="flex items-center gap-1 text-slate-400">
                      <Mail className="w-3 h-3" /> {contact.email}
                    </span>
                  </div>
                </div>
              ))}
              {internalContacts.length === 0 && (
                <p className="text-slate-400 text-center py-8">No internal contacts added</p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* External Contacts */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5" /> External Contacts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              {externalContacts.map(contact => (
                <div key={contact.id} className="p-3 rounded-lg bg-slate-900/50 mb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-medium">{contact.name}</p>
                      <p className="text-sm text-slate-400">{contact.organization || contact.title}</p>
                      <p className="text-xs text-emerald-400 capitalize">{contact.category?.replace('_', ' ')}</p>
                    </div>
                    {contact.is_primary && <Badge className="bg-emerald-500/20 text-emerald-300">Primary</Badge>}
                  </div>
                  <div className="mt-2 flex gap-4 text-sm">
                    <span className="flex items-center gap-1 text-slate-400">
                      <Phone className="w-3 h-3" /> {contact.primary_phone}
                    </span>
                  </div>
                </div>
              ))}
              {externalContacts.length === 0 && (
                <p className="text-slate-400 text-center py-8">No external contacts added</p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
